import { Users } from 'lucide-react';
import { useState } from 'react';

import { useCuponsDiscussaoAdminControllerListarUsos } from '@/api/generated/admin-cupons-de-desconto-discussão/admin-cupons-de-desconto-discussão';
import type { CupomDiscussaoResponse } from '@/api/generated/model';
import { EstadoErro, EstadoVazio } from '@/shared/components/estados';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Paginacao } from '@/shared/ui/pagination';
import { Skeleton } from '@/shared/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';

interface Props {
  /** `null` = fechado. */
  cupom: CupomDiscussaoResponse | null;
  aoFechar: () => void;
}

function formatarDataHora(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR');
}

/**
 * `GET /admin/cupons-discussao/:id/usos` — pro suporte responder "por que
 * esse médico não consegue usar de novo" sem abrir o banco.
 */
export function UsosDoCupomDialogo({ cupom, aoFechar }: Props) {
  const [pagina, setPagina] = useState(1);

  const { data, isPending, isError, refetch } = useCuponsDiscussaoAdminControllerListarUsos(
    cupom?.id ?? '',
    { pagina, porPagina: 10, ordenarPor: 'criadoEm', ordem: 'desc' },
    { query: { enabled: Boolean(cupom) } },
  );

  return (
    <Dialog
      open={Boolean(cupom)}
      onOpenChange={(v) => {
        if (!v) {
          aoFechar();
          setPagina(1);
        }
      }}
    >
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Usos de {cupom?.codigo}</DialogTitle>
          <DialogDescription>
            {cupom?.usosContados ?? 0} de {cupom?.usosMaximos ?? '∞'} usos — quem usou, quando e em qual
            discussão.
          </DialogDescription>
        </DialogHeader>

        {isPending ? (
          <div className="space-y-2">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-9 rounded-md" />
            ))}
          </div>
        ) : isError || !data ? (
          <EstadoErro aoTentarDeNovo={() => void refetch()} />
        ) : data.itens.length === 0 ? (
          <EstadoVazio
            icone={Users}
            titulo="Ninguém usou este cupom ainda"
            descricao="Assim que um médico usar, aparece aqui."
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Médico</TableHead>
                    <TableHead>Discussão</TableHead>
                    <TableHead>Desconto</TableHead>
                    <TableHead>Quando</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.itens.map((uso) => (
                    <TableRow key={uso.id}>
                      <TableCell className="text-foreground">{uso.profileNome}</TableCell>
                      <TableCell className="text-muted-foreground">{uso.discussaoCodigo}</TableCell>
                      <TableCell className="text-muted-foreground">{uso.percentualAplicado}%</TableCell>
                      <TableCell className="text-muted-foreground whitespace-nowrap">
                        {formatarDataHora(uso.criadoEm)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <Paginacao
              pagina={data.pagina}
              totalPaginas={data.totalPaginas}
              total={data.total}
              porPagina={data.porPagina}
              aoMudarPagina={setPagina}
            />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
